import { configureStore } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';
import { appReducer } from './slices/app/reducer/appSlice';
import { documentReducer } from './slices/document/reducer/documentSlice';
import { activityLogsReducer } from './slices/activityLogs/reducer/activityLogsSlice';
import { permissionsReducer } from './slices/permissions/reducer/permissionsSlice';
import { kidReducer } from './slices/kid/kidSlice';
import { rtiReducer } from './slices/RTI/reducer/rtiSlice';
import { revenueReducer } from './slices/revenueFilings/reducer/revenueSlice';
import { hmrcUpdatesReducer } from './slices/hmrcUpdates/reducer/hmrcUpdatesSlice';
import { statutoryReducer } from './slices/statutory/reducer/statutorySlice';
import { rpnReducer } from './slices/rpn/reducer/rpnSlice';
import { exportReducer } from './slices/export/reducer/exportSlice';
import { notificationReducer } from './slices/notification/reducer/notificationSlice';
import { assignmentReducer } from './slices/assignment/reducer/assignmentSlice';
import { commonReducer } from './slices/common/reducer/commonSlice';
import { prospectReducer } from './slices/prospect/reducer/prospectSlice';
import { bulkUpdateReducer } from './slices/bulkUpdate/reducer/bulkUpdateSlice';
import { analyticsReducer } from './slices/analytics/reducer/analyticsSlice';
import { agencyReducer } from './slices/agency/reducer/agencySlice';
import { paymentSettingReducer } from './slices/paymentSetting/reducer/paymentSettingSlice';
import { timesheetsReducer } from './slices/timesheets/reducer/timesheetsSlice';
import { payrollReducer } from './slices/payroll/reducer/payrollSlice';
import { importReducer } from './slices/import/reducer/importSlice';
import { reportReducer } from './slices/report/reducer/reportSlice';
import { employeeReducer } from './slices/employee/reducer/employeeSlice';
import { companyReducer } from './slices/company/reducer/companySlice';
import { dashboardReducer } from './slices/dashboard/reducer/dashboardSlice';
import { aepnReducer } from './slices/aepn/reducer/aepnSlice';
import { messageReducer } from './slices/message/reducer/messageSlice';
import { employeeMessageReducer } from './slices/employeeMessage/reducer/employeeMessageSlice';
import { superAdminReducer } from './slices/superAdmin/reducer/superAdminSlice';

const combinedReducer = combineReducers({
  appStore: appReducer,
  documentStore: documentReducer,
  activityLogsStore: activityLogsReducer,
  permissionsStore: permissionsReducer,
  kid: kidReducer,
  rtiStore: rtiReducer,
  revenueStore: revenueReducer,
  hmrcUpdatesStore: hmrcUpdatesReducer,
  statutoryStore: statutoryReducer,
  rpnStore: rpnReducer,
  exportStore: exportReducer,
  notificationStore: notificationReducer,
  assignmentStore: assignmentReducer,
  commonStore: commonReducer,
  prospectStore: prospectReducer,
  bulkUpdateStore: bulkUpdateReducer,
  analyticsStore: analyticsReducer,
  agencyStore: agencyReducer,
  paymentSettingStore: paymentSettingReducer,
  timesheetsStore: timesheetsReducer,
  payrollStore: payrollReducer,
  importStore: importReducer,
  reportStore: reportReducer,
  employeeStore: employeeReducer,
  companyStore: companyReducer,
  dashboardStore: dashboardReducer,
  aepnStore: aepnReducer,
  messageStore: messageReducer,
  employeeMessageStore: employeeMessageReducer,
  superAdminStore: superAdminReducer,
});

// Safe reducer wrapper: catches errors in reducers to prevent React crashes.
// When a reducer throws (e.g., .map() on undefined API data), we return the
// previous state instead of letting the error propagate to the error boundary.
const rootReducer: typeof combinedReducer = (state, action) => {
  try {
    return combinedReducer(state, action);
  } catch (error) {
    console.error(
      `[Redux Error] Action "${action.type}" caused an error in reducer:`,
      error,
    );
    // Return previous state — the UI continues working with stale data
    // instead of crashing with "Something went wrong".
    // On first call (state=undefined), fall back to initial state from a clean run.
    if (state === undefined) {
      return combinedReducer(undefined, { type: '@@SAFE_INIT' });
    }
    return state as ReturnType<typeof combinedReducer>;
  }
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoreActions: true,
      },
    }),
});

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;
export type AppState = ReturnType<typeof store.getState>;
export type AppStore = typeof store;
export type AppStoreDispatch = typeof store.dispatch;
export type AppStoreState = ReturnType<typeof rootReducer>;
export type AppStoreGetState = () => AppStoreState;
export type AppStoreDispatchType = () => AppStoreDispatch;
export type AppStoreGetStateType = () => AppStoreGetState;
